// components/Batches/AddOrEditBatchPage.js
import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import { Save, Layers, Calendar, Clock, Monitor, Users, BookOpen, Video, Zap, ShieldOff, AlertTriangle } from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, PageHeader, FormActionBar, ResetButton,
} from "../ui";
import { useBranch } from "../../contexts/BranchContext";
import { usePlan } from "../../contexts/PlanContext";
import "./Batches.scss";

const assignmentsOptions = ["JavaScript", "Java", "Python", "C++", "Go", "Data Structures", "Algorithms"];

const initialState = {
  course: "",
  name: "",
  instructor: "",
  instructors: [],
  startDate: "",
  endDate: "",
  batchType: "WEEKDAY",
  meetingDays: [],
  minAttendancePct: "",
  startTime: "",
  endTime: "",
  mode: "Offline",
  status: "Upcoming",
  strength: "",
  maxStudents: 100,
  meetingLink: "",
  zoomMeetingId: "",
  zoomPasscode: "",
  assignments: false,
  assignmentStack: "",
  room: "",
  branchId: "",
  expiryDate: "",
  showTrainerPublicly: true,
};

const AddOrEditBatchPage = () => {
  const [form, setForm] = useState(initialState);
  const [courseOptions, setCourseOptions] = useState([]);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [loading, setLoading] = useState(false);
  const { batchId } = useParams();
  // "Add batch" on a course page opens /batches/add?course=<id>. If that course
  // is not offered at the branch picked here, the check below clears it.
  const [searchParams] = useSearchParams();
  const presetCourse = searchParams.get("course") || "";
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState([]);
  const [zoomAutoCreate, setZoomAutoCreate] = useState(false);
  const [zoomAutoCreated, setZoomAutoCreated] = useState(false);
  const [isPlatformHostedZoom, setIsPlatformHostedZoom] = useState(false);
  const [scheduleConflict, setScheduleConflict] = useState(null);
  const { branches, isMultiBranch } = useBranch();
  const { hasPlanFeature } = usePlan();
  const attendanceOn = hasPlanFeature("attendanceManagement");

  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Pristine form snapshot for the Reset action (set on initial load).
  const pristineRef = useRef(initialState);

  const setFieldError = (name, msg) => setFieldErrors(prev => ({ ...prev, [name]: msg }));
  const clearFieldError = (name) => setFieldErrors(prev => { const n = { ...prev }; delete n[name]; return n; });

  const loadInstructorList = async () => {
    try {
      const { data } = await apiClient.get("/instructors/list");
      const instructors = data.instructors || [];
      setInstructors(instructors);
    } catch (err) {
      setToast({ show: true, message: "Failed to load instructors.", type: "error" });
    }
  };

  useEffect(() => {
    loadInstructorList();
    apiClient.get("/institute/zoom-settings")
      .then(({ data }) => {
        setZoomAutoCreate(data.zoomMode === 'v2' && !!data.v2AutoCreateMeeting && !!data.v2Connected);
        setIsPlatformHostedZoom(data.zoomMode === 'platformHosted');
      })
      .catch(() => {});
  },[]);

  // Courses are fetched with the branch chosen ON THIS FORM, not the one in the
  // top-nav selector — the batch's own branch decides which courses may run in
  // it. Sending the header explicitly is what makes the two independent; the
  // API then returns courses offered at that branch plus every institute-wide
  // one, and refuses the save if the pairing is wrong anyway.
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const { data } = await apiClient.get("/courses/list", {
          headers: form.branchId ? { "X-Branch-Id": form.branchId } : undefined,
        });
        setCourseOptions((data.courses || []).map(c => ({ value: c._id, label: c.name, shortCode: c.shortCode || '' })));
      } catch {
        setCourseOptions([]);
      }
    };
    fetchCourses();
  }, [form.branchId]);

  // A branch change can strand an already-picked course that is not offered
  // there. Clear it rather than leaving an invalid pair on screen for the
  // backend to reject on submit.
  useEffect(() => {
    if (!form.course || !courseOptions.length) return;
    if (!courseOptions.some(c => c.value === form.course)) {
      setForm(prev => ({ ...prev, course: "" }));
    }
  }, [courseOptions, form.course]);

  useEffect(() => {
    if (batchId) {
      setLoading(true);
      (async () => {
        try {
          const { data } = await apiClient.get(`/batches/${batchId}`);
          const batchData = data.batch || data;
          // Build instructors array from populated data
          let instrIds = [];
          if (Array.isArray(batchData.instructors) && batchData.instructors.length > 0) {
            instrIds = batchData.instructors.map(i => (typeof i === 'object' ? i._id : i));
          } else if (batchData.instructor) {
            instrIds = [typeof batchData.instructor === 'object' ? batchData.instructor._id : batchData.instructor];
          }
          const built = {
            ...initialState,
            ...batchData,
            instructor: instrIds[0] || "",
            instructors: instrIds,
            course: typeof batchData.course === "object" ? batchData.course._id : batchData.course || "",
            startDate: batchData.startDate?.substring(0, 10) || "",
            endDate: batchData.endDate?.substring(0, 10) || "",
            expiryDate: batchData.expiryDate?.substring(0, 10) || "",
            branchId: batchData.branchId?._id || batchData.branchId || "",
          };
          setForm(built);
          pristineRef.current = built;
        } catch {
          setToast({ show: true, message: "Failed to load batch.", type: "error" });
        }
        setLoading(false);
      })();
    } else {
      const fresh = presetCourse ? { ...initialState, course: presetCourse } : initialState;
      setForm(fresh);
      pristineRef.current = fresh;
    }
    // eslint-disable-next-line
  }, [batchId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      // Live cross-field validation — dates
      if (name === "endDate") {
        if (prev.startDate && value && value < prev.startDate) {
          setFieldError("endDate", "End date cannot be before start date.");
        } else {
          clearFieldError("endDate");
        }
      }
      if (name === "startDate") {
        if (prev.endDate && value && prev.endDate < value) {
          setFieldError("endDate", "End date cannot be before start date.");
        } else {
          clearFieldError("endDate");
        }
        if (value) clearFieldError("startDate");
      }

      // Live cross-field validation — times
      if (name === "endTime") {
        if (prev.startTime && value && value <= prev.startTime) {
          setFieldError("endTime", "End time must be after start time.");
        } else {
          clearFieldError("endTime");
        }
      }
      if (name === "startTime") {
        if (prev.endTime && value && prev.endTime <= value) {
          setFieldError("endTime", "End time must be after start time.");
        } else {
          clearFieldError("endTime");
        }
      }

      // Live range validation — capacity fields
      if (name === "strength") {
        const n = Number(value);
        if (value !== "" && (n < 0 || n > 100)) {
          setFieldError("strength", "Strength must be between 0 and 100.");
        } else {
          clearFieldError("strength");
        }
      }
      if (name === "maxStudents") {
        const n = Number(value);
        if (value !== "" && (n < 0 || n > 100)) {
          setFieldError("maxStudents", "Max students must be between 0 and 100.");
        } else {
          clearFieldError("maxStudents");
        }
      }

      // Clear required-field errors as soon as user provides a value
      if ((name === "course" || name === "name") && value) clearFieldError(name);

      return next;
    });
  };

  // ── Scheduling policy (platform-wide, set by the SaaS admin) ────────────
  // Fetched rather than hardcoded so the form shows the same numbers the API
  // enforces. Falls back to the shipped defaults if the call fails — a form
  // that cannot load a hint must still be usable.
  // Defaults are the INERT ones. The platform ships with timed batches switched
  // off, and a form that demanded times the API is not enforcing would block
  // institutes over a rule that does not exist yet.
  const [schedulingRules, setSchedulingRules] = useState({
    minLeadTimeMinutes: 30,
    startBufferMinutes: 15,
    requireBatchTiming: false,
    enforceLeadTime: false,
    enforceClassWindow: false,
  });

  useEffect(() => {
    let alive = true;
    apiClient.get("/institute/scheduling-rules")
      .then(({ data }) => { if (alive && data) setSchedulingRules(data); })
      .catch(() => { /* keep defaults */ });
    return () => { alive = false; };
  }, []);

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    if (!value) {
      const labels = {
        course: "Course", name: "Batch name", startDate: "Start date", endDate: "End date",
        // Only required once the platform actually enforces timed batches.
        ...(schedulingRules.requireBatchTiming ? {
          startTime: "Class start time", endTime: "Class end time",
        } : {}),
      };
      if (labels[name]) setFieldError(name, `${labels[name]} is required.`);
    }
  };

  // Toggle a weekday (0=Sun … 6=Sat) in the batch's meeting-day pattern.
  const toggleMeetingDay = (dayNum) => {
    setForm((prev) => {
      const cur = Array.isArray(prev.meetingDays) ? prev.meetingDays : [];
      const next = cur.includes(dayNum)
        ? cur.filter((d) => d !== dayNum)
        : [...cur, dayNum].sort((a, b) => a - b);
      return { ...prev, meetingDays: next };
    });
  };

  const handleInstructorsChange = (selectedOptions) => {
    const ids = (selectedOptions || []).map(opt => opt.value);
    setForm(prev => ({
      ...prev,
      instructors: ids,
      instructor: ids[0] || "",
    }));
    if (ids.length > 0) clearFieldError("instructors");
    else if (touched.instructors) setFieldError("instructors", "At least one instructor is required.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ── Class timing is mandatory ─────────────────────────────────────────
    // Not cosmetic: without it the platform cannot tell when this batch needs
    // a virtual classroom, so it cannot reserve one or warn that the pool is
    // about to run out. The API rejects the same case.
    if (schedulingRules.requireBatchTiming && (!form.startTime || !form.endTime)) {
      if (!form.startTime) setFieldError("startTime", "Class start time is required.");
      if (!form.endTime) setFieldError("endTime", "Class end time is required.");
      setTouched(prev => ({ ...prev, startTime: true, endTime: true }));
      setToast({
        show: true,
        message: "Class start time and end time are required — they decide when the class can be started.",
        type: "error",
      });
      return;
    }

    setLoading(true);
    try {
      // Build payload — send both instructor (primary) and instructors array
      const payload = {
        ...form,
        instructor: form.instructors[0] || form.instructor || "",
        instructors: form.instructors,
      };
      let hasConflict = false;
      if (batchId) {
        const { data } = await apiClient.put(`/batches/${batchId}`, payload);
        if (data.scheduleConflict) {
          hasConflict = true;
          setScheduleConflict(data.scheduleConflict);
          setToast({ show: true, message: "Batch updated — but there is a schedule conflict. See warning below.", type: "warning" });
        } else {
          setScheduleConflict(null);
          setToast({ show: true, message: "Batch updated successfully!", type: "success" });
        }
      } else {
        const { data } = await apiClient.post("/batches/add", payload);
        if (data.scheduleConflict) {
          hasConflict = true;
          setScheduleConflict(data.scheduleConflict);
        } else {
          setScheduleConflict(null);
        }
        if (data.zoomAutoCreated) {
          setZoomAutoCreated(true);
          if (data.batch) {
            setForm(prev => ({
              ...prev,
              zoomMeetingId: data.batch.zoomMeetingId || prev.zoomMeetingId,
              zoomPasscode: data.batch.zoomPasscode || prev.zoomPasscode,
              meetingLink: data.batch.meetingLink || prev.meetingLink,
            }));
          }
          setToast({ show: true, message: "Batch added with Zoom meeting auto-created!", type: "success" });
        } else {
          setToast({ show: true, message: data.scheduleConflict ? "Batch added — but there is a schedule conflict. See warning below." : "Batch added successfully!", type: data.scheduleConflict ? "warning" : "success" });
        }
        setTimeout(() => { if (!data.zoomAutoCreated) setForm(initialState); }, 300);
      }
      if (!hasConflict) setTimeout(() => navigate("/batches"), 1500);
    } catch (err) {
      // Point at the offending fields for the two scheduling rules, so the
      // message is not the only clue about what to change.
      const code = err?.response?.data?.errorCode;
      if (code === "BATCH_TIMING_REQUIRED") {
        if (!form.startTime) setFieldError("startTime", "Class start time is required.");
        if (!form.endTime) setFieldError("endTime", "Class end time is required.");
      } else if (code === "LEAD_TIME_TOO_SHORT") {
        setFieldError("startDate", "Too soon — pick a later start.");
        setFieldError("startTime", "Too soon — pick a later time.");
      }
      setToast({ show: true, message: err?.response?.data?.message || "Failed to save batch.", type: "error" });
    }
    setLoading(false);
  };

  const handleReset = () => {
    setForm(pristineRef.current);
    setFieldErrors({});
    setTouched({});
    setScheduleConflict(null);
  };

  if (loading && batchId) {
    return (
      <div className="bf-loading">
        <div className="spinner-border" />
      </div>
    );
  }

  const selectedCourse = courseOptions.find(c => c.value === form.course);
  const coursePrefix = selectedCourse?.shortCode || '';

  const strippedName = coursePrefix && form.name?.startsWith(`${coursePrefix}_`)
    ? form.name.slice(coursePrefix.length + 1)
    : form.name;
  const displayBatchName = coursePrefix && strippedName
    ? `${coursePrefix}_${strippedName}`
    : strippedName;

  // Build react-select options & value for instructors
  const instructorSelectOptions = instructors.map(inst => ({
    value: inst._id,
    label: inst.name,
  }));
  const selectedInstructors = instructorSelectOptions.filter(opt =>
    form.instructors.includes(opt.value)
  );

  return (
    <form className="bf-page has-form-actionbar" onSubmit={handleSubmit}>
      {/* ── Header (portaled into the top nav) ── */}
      <PageHeader
        icon={Layers}
        title={batchId ? "Edit Batch" : "Add New Batch"}
        description={batchId ? `Editing ${form.name || "batch"}` : "Create a new batch for your course"}
        backButton
        onBack={() => navigate("/batches")}
      />

      {/* ── Schedule Conflict Warning ── */}
      {scheduleConflict && (
        <div className="bf-alert bf-alert--warning bf-schedule-conflict">
          <AlertTriangle size={16} />
          <div>
            <strong>Schedule Conflict Detected</strong>
            <p className="bf-alert__text">{scheduleConflict.message}</p>
          </div>
        </div>
      )}

      {/* ── Form Body ── */}
      <div className="bf-body">
        {/* ── Basic Information ── */}
        <AppCard className="bf-card">
          <AppCardHeader>
            <AppCardTitle className="bf-section-title">
              <Layers size={16} />
              Basic Information
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="bf-grid">
              <div className="bf-field">
                <label className="bf-label">Course *</label>
                <select
                  className={`bf-select${fieldErrors.course ? ' bf-input--error' : ''}`}
                  name="course"
                  value={form.course}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                >
                  <option value="">Select course</option>
                  {courseOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {fieldErrors.course && <span className="bf-field-error">{fieldErrors.course}</span>}
              </div>
              {isMultiBranch && branches.length > 0 && (
                <div className="bf-field">
                  <label className="bf-label">Branch</label>
                  <select
                    className="bf-select"
                    name="branchId"
                    value={form.branchId}
                    onChange={handleChange}
                  >
                    <option value="">No branch</option>
                    {branches.filter(b => b.isActive).map(b => (
                      <option key={b._id} value={b._id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="bf-field">
                <label className="bf-label">Batch Name *</label>
                <div className="bf-input-group">
                  {coursePrefix && (
                    <span className="bf-input-prefix">{coursePrefix}_</span>
                  )}
                  <input
                    className={`bf-input${coursePrefix ? ' bf-input--prefixed' : ''}${fieldErrors.name ? ' bf-input--error' : ''}`}
                    name="name"
                    value={strippedName}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, name: e.target.value }));
                      if (e.target.value) clearFieldError('name');
                    }}
                    onBlur={(e) => {
                      setTouched(prev => ({ ...prev, name: true }));
                      if (!e.target.value) setFieldError('name', 'Batch name is required.');
                    }}
                    placeholder={coursePrefix ? 'e.g., 12' : 'e.g., Batch 8'}
                    required
                  />
                </div>
                {fieldErrors.name && <span className="bf-field-error">{fieldErrors.name}</span>}
                {!fieldErrors.name && coursePrefix && strippedName && (
                  <span className="bf-hint">
                    Will be saved as: <strong>{displayBatchName}</strong>
                  </span>
                )}
              </div>
              <div className="bf-field bf-field--full">
                <label className="bf-label">Instructors *</label>
                <Select
                  isMulti
                  options={instructorSelectOptions}
                  value={selectedInstructors}
                  onChange={handleInstructorsChange}
                  onBlur={() => {
                    setTouched(prev => ({ ...prev, instructors: true }));
                    if (form.instructors.length === 0) setFieldError('instructors', 'At least one instructor is required.');
                  }}
                  placeholder="Select one or more instructors..."
                  className={`bf-react-select${fieldErrors.instructors ? ' bf-react-select--error' : ''}`}
                  classNamePrefix="bf-rs"
                  noOptionsMessage={() => "No instructors found"}
                  isClearable
                />
                {fieldErrors.instructors && <span className="bf-field-error">{fieldErrors.instructors}</span>}
                {!fieldErrors.instructors && selectedInstructors.length > 1 && (
                  <span className="bf-hint">
                    <Users size={12} /> {selectedInstructors.length} instructors assigned. First instructor is the primary.
                  </span>
                )}
              </div>
              <div className="bf-field bf-field--full">
                <label className="bf-label">Trainer visibility on website</label>
                <div className="bf-toggle-row">
                  <label className="bf-toggle">
                    <input
                      type="checkbox"
                      checked={!!form.showTrainerPublicly}
                      onChange={(e) => setForm(prev => ({ ...prev, showTrainerPublicly: e.target.checked }))}
                    />
                    <span className="bf-toggle-slider" />
                  </label>
                  <span className="bf-toggle-label">
                    {form.showTrainerPublicly
                      ? 'Trainer name is visible on the public CMS website'
                      : 'Trainer name is hidden from the public CMS website'}
                  </span>
                </div>
              </div>
              <div className="bf-field">
                <label className="bf-label">Status</label>
                <select
                  className="bf-select"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                >
                  <option>Upcoming</option>
                  <option>Ongoing</option>
                  <option>Completed</option>
                </select>
              </div>
              <div className="bf-field">
                <label className="bf-label">Mode *</label>
                <select
                  className="bf-select"
                  name="mode"
                  value={form.mode}
                  onChange={handleChange}
                  required
                >
                  <option>Offline</option>
                  <option>Online</option>
                  <option>Both</option>
                </select>
              </div>
            </div>
          </AppCardContent>
        </AppCard>

        {/* ── Schedule & Timing ── */}
        <AppCard className="bf-card">
          <AppCardHeader>
            <AppCardTitle className="bf-section-title">
              <Calendar size={16} />
              Schedule & Timing
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="bf-grid">
              <div className="bf-field">
                <label className="bf-label">Start Date *</label>
                <input
                  className={`bf-input${fieldErrors.startDate ? ' bf-input--error' : ''}`}
                  type="date"
                  name="startDate"
                  value={form.startDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
                {fieldErrors.startDate && <span className="bf-field-error">{fieldErrors.startDate}</span>}
              </div>
              <div className="bf-field">
                <label className="bf-label">End Date *</label>
                <input
                  className={`bf-input${fieldErrors.endDate ? ' bf-input--error' : ''}`}
                  type="date"
                  name="endDate"
                  value={form.endDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  min={form.startDate || undefined}
                  disabled={!form.startDate}
                />
                {fieldErrors.endDate && <span className="bf-field-error">{fieldErrors.endDate}</span>}
              </div>
              {/* Course Expiry Date — visible when editing a completed batch */}
              {batchId && form.status === "Completed" && (
                <div className="bf-field">
                  <label className="bf-label">
                    <ShieldOff size={14} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                    Content Expiry Date
                    <span className="bf-label-hint">(optional)</span>
                  </label>
                  <input
                    className="bf-input"
                    type="date"
                    name="expiryDate"
                    value={form.expiryDate}
                    onChange={handleChange}
                    min={form.endDate || undefined}
                  />
                  <span className="bf-hint">
                    Students lose access to content after this date. Leave empty for no restriction.
                  </span>
                </div>
              )}
              <div className="bf-field bf-field--full">
                <label className="bf-label">Batch Type</label>
                <div className="bf-radio-group">
                  <label className={`bf-radio-card ${form.batchType === "WEEKDAY" ? "bf-radio-card--active" : ""}`}>
                    <input
                      type="radio"
                      name="batchType"
                      value="WEEKDAY"
                      checked={form.batchType === "WEEKDAY"}
                      onChange={handleChange}
                    />
                    <div className="bf-radio-card__content">
                      <span className="bf-radio-card__label">Weekday Batch</span>
                      <span className="bf-radio-card__desc">Mon – Sat</span>
                    </div>
                  </label>
                  <label className={`bf-radio-card ${form.batchType === "WEEKEND" ? "bf-radio-card--active" : ""}`}>
                    <input
                      type="radio"
                      name="batchType"
                      value="WEEKEND"
                      checked={form.batchType === "WEEKEND"}
                      onChange={handleChange}
                    />
                    <div className="bf-radio-card__content">
                      <span className="bf-radio-card__label">Weekend Batch</span>
                      <span className="bf-radio-card__desc">Sat – Sun</span>
                    </div>
                  </label>
                </div>
              </div>
              {attendanceOn && (
                <>
              <div className="bf-field bf-field--full">
                <label className="bf-label">Meeting Days <span className="bf-label-optional">(optional)</span></label>
                <div className="bf-weekday-row">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((lbl, idx) => {
                    const on = (form.meetingDays || []).includes(idx);
                    return (
                      <button
                        type="button"
                        key={lbl}
                        className={`bf-weekday${on ? " bf-weekday--on" : ""}${idx === 0 ? " bf-weekday--sun" : ""}`}
                        onClick={() => toggleMeetingDay(idx)}
                        title={lbl}
                        aria-pressed={on}
                      >
                        {lbl[0]}
                      </button>
                    );
                  })}
                </div>
                <span className="bf-hint">
                  Days this batch meets — the denominator for attendance %. Leave empty to use the batch type
                  ({form.batchType === "WEEKEND" ? " Sat–Sun" : " institute working days"}).
                </span>
              </div>
              <div className="bf-field">
                <label className="bf-label">Minimum Attendance %</label>
                <input
                  className="bf-input"
                  type="number"
                  name="minAttendancePct"
                  min={0}
                  max={100}
                  value={form.minAttendancePct ?? ""}
                  onChange={handleChange}
                  placeholder="Institute default"
                />
                <span className="bf-hint">Eligibility threshold for the attendance register. Leave empty to use the institute policy.</span>
              </div>
                </>
              )}
              <div className="bf-field">
                <label className="bf-label">Class Start Time{schedulingRules.requireBatchTiming ? " *" : ""}</label>
                <input
                  className={`bf-input${fieldErrors.startTime ? ' bf-input--error' : ''}`}
                  type="time"
                  name="startTime"
                  value={form.startTime}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required={!!schedulingRules.requireBatchTiming}
                />
                {fieldErrors.startTime && <span className="bf-field-error">{fieldErrors.startTime}</span>}
                {!fieldErrors.startTime && schedulingRules.enforceClassWindow && (
                  <span className="bf-hint">
                    Trainers can open the classroom {schedulingRules.startBufferMinutes} min early,
                    and only until the end time below.
                  </span>
                )}
                {!fieldErrors.startTime && !schedulingRules.enforceClassWindow && (
                  <span className="bf-hint">
                    Set the time this batch actually meets so it can be scheduled and started reliably.
                  </span>
                )}
              </div>
              <div className="bf-field">
                <label className="bf-label">Class End Time{schedulingRules.requireBatchTiming ? " *" : ""}</label>
                <input
                  className={`bf-input${fieldErrors.endTime ? ' bf-input--error' : ''}`}
                  type="time"
                  name="endTime"
                  value={form.endTime}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min={form.startTime || undefined}
                  disabled={!form.startTime}
                  required={!!schedulingRules.requireBatchTiming}
                />
                {fieldErrors.endTime && <span className="bf-field-error">{fieldErrors.endTime}</span>}
                {!fieldErrors.endTime && schedulingRules.enforceLeadTime && (
                  <span className="bf-hint">
                    Schedule at least {schedulingRules.minLeadTimeMinutes} min ahead so a virtual
                    classroom can be reserved.
                  </span>
                )}
              </div>
            </div>
          </AppCardContent>
        </AppCard>

        {/* ── Capacity & Assignments ── */}
        <AppCard className="bf-card">
          <AppCardHeader>
            <AppCardTitle className="bf-section-title">
              <BookOpen size={16} />
              Capacity & Assignments
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="bf-grid">
              <div className="bf-field">
                <label className="bf-label">Strength</label>
                <input
                  className={`bf-input${fieldErrors.strength ? ' bf-input--error' : ''}`}
                  type="number"
                  name="strength"
                  value={form.strength}
                  onChange={handleChange}
                  placeholder="e.g., 40"
                  min={0}
                  max={100}
                />
                {fieldErrors.strength && <span className="bf-field-error">{fieldErrors.strength}</span>}
              </div>
              <div className="bf-field">
                <label className="bf-label">Max Students
                  <span className="bf-label-hint">(0 = use plan default)</span>
                </label>
                <input
                  className={`bf-input${fieldErrors.maxStudents ? ' bf-input--error' : ''}`}
                  type="number"
                  name="maxStudents"
                  value={form.maxStudents}
                  onChange={handleChange}
                  placeholder="0"
                  min={0}
                  max={100}
                />
                {fieldErrors.maxStudents && <span className="bf-field-error">{fieldErrors.maxStudents}</span>}
              </div>
              <div className="bf-field">
                <label className="bf-label">Assignments</label>
                <select
                  className="bf-select"
                  name="assignments"
                  value={form.assignments}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              {(form.assignments === true || form.assignments === "true") && (
                <div className="bf-field">
                  <label className="bf-label">Assignment Stack</label>
                  <select
                    className="bf-select"
                    name="assignmentStack"
                    value={form.assignmentStack}
                    onChange={handleChange}
                  >
                    <option value="">Select Stack</option>
                    {assignmentsOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </AppCardContent>
        </AppCard>

        {/* ── Meeting / Classroom ── */}
        {form.mode !== "Offline" && (
          <AppCard className="bf-card">
            <AppCardHeader>
              <AppCardTitle className="bf-section-title">
                <Video size={16} />
                Meeting / Classroom
              </AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="bf-grid">
                <div className="bf-field">
                  <label className="bf-label">Meeting Link</label>
                  <input
                    className="bf-input"
                    name="meetingLink"
                    value={form.meetingLink}
                    onChange={handleChange}
                    placeholder={isPlatformHostedZoom ? "Managed by platform host pool" : zoomAutoCreate && !batchId ? "Will be auto-created" : "Paste online meeting link"}
                    disabled={isPlatformHostedZoom || (zoomAutoCreate && !batchId)}
                  />
                </div>
                <div className="bf-field">
                  <label className="bf-label">Zoom Meeting ID</label>
                  <input
                    className="bf-input"
                    name="zoomMeetingId"
                    type="number"
                    value={form.zoomMeetingId}
                    onChange={handleChange}
                    placeholder={isPlatformHostedZoom ? "Assigned automatically" : zoomAutoCreate && !batchId ? "Will be auto-created" : "Zoom meeting ID"}
                    disabled={isPlatformHostedZoom || (zoomAutoCreate && !batchId)}
                  />
                </div>
                <div className="bf-field">
                  <label className="bf-label">Zoom Passcode</label>
                  <input
                    className="bf-input"
                    name="zoomPasscode"
                    value={form.zoomPasscode}
                    onChange={handleChange}
                    placeholder={isPlatformHostedZoom ? "Assigned automatically" : zoomAutoCreate && !batchId ? "Will be auto-created" : "Zoom passcode"}
                    disabled={isPlatformHostedZoom || (zoomAutoCreate && !batchId)}
                  />
                </div>
                {form.mode !== "Online" && (
                  <div className="bf-field">
                    <label className="bf-label">Room / Classroom</label>
                    <input
                      className="bf-input"
                      name="room"
                      value={form.room}
                      onChange={handleChange}
                      placeholder="Room number or name"
                    />
                  </div>
                )}
              </div>
              {isPlatformHostedZoom && (
                <div className="bf-alert bf-alert--info">
                  <Zap size={14} />
                  <span><strong>Platform Host Pool:</strong> Meeting details are managed automatically by the platform. No manual Zoom setup needed.</span>
                </div>
              )}
              {!isPlatformHostedZoom && zoomAutoCreate && !batchId && (
                <div className="bf-alert bf-alert--info">
                  <Zap size={14} />
                  <span><strong>Auto-Create Enabled:</strong> A Zoom meeting will be automatically created when you save this batch.</span>
                </div>
              )}
              {zoomAutoCreated && (
                <div className="bf-alert bf-alert--success">
                  <Zap size={14} />
                  <span><strong>Zoom meeting auto-created!</strong> Meeting ID: {form.zoomMeetingId}, Passcode: {form.zoomPasscode}</span>
                </div>
              )}
            </AppCardContent>
          </AppCard>
        )}

        {/* ── Room (offline only) ── */}
        {form.mode === "Offline" && (
          <AppCard className="bf-card">
            <AppCardHeader>
              <AppCardTitle className="bf-section-title">
                <Monitor size={16} />
                Classroom
              </AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="bf-grid">
                <div className="bf-field">
                  <label className="bf-label">Room / Classroom</label>
                  <input
                    className="bf-input"
                    name="room"
                    value={form.room}
                    onChange={handleChange}
                    placeholder="Room number or name"
                  />
                </div>
              </div>
            </AppCardContent>
          </AppCard>
        )}
      </div>

      {/* ── Fixed bottom action bar ── */}
      <FormActionBar>
        <AppButton variant="outline" type="button" onClick={() => navigate("/batches")}>
          Cancel
        </AppButton>
        <ResetButton onReset={handleReset} />
        <AppButton variant="primary" type="submit" disabled={loading}>
          <Save size={16} />
          {loading ? (batchId ? "Updating..." : "Saving...") : (batchId ? "Update Batch" : "Save Batch")}
        </AppButton>
      </FormActionBar>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </form>
  );
};

export default AddOrEditBatchPage;
